import React, { Suspense, useCallback, useEffect, lazy, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tab, Tabs, useContainerWidth } from '@fs/zion-ui'
import { useFeatureFlags } from '@fs/zion-flags'
import { Switch, Route, AuthRoute, NotFound } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { NoticeLoading } from '@fs/zion-icon'
import { getSessionId, getAnonSession } from '@fs/zion-session'
import { casService, urlUtils } from '@fs/zion-memories/memories-authorized-use-only'
import { targetEnv } from '@fs/zion-config'
import { useGroupsFlags } from './hooks'
import { GalleryProvider } from './pages/Gallery/GalleryContext'
import Feedback from './pages/Feedback'

// Dynamically load components to reduce bundle size
// https://reactjs.org/docs/react-api.html#reactlazy

const Overview = lazy(() => import('./pages/Overview'))
const Gallery = lazy(() => import('./pages/Gallery'))
const People = lazy(() => import('./pages/People'))
const Find = lazy(() => import('./pages/Find'))
const TreeMemoriesPage = lazy(() => import('./pages/TreeMemories'))
const MemoryPage = lazy(() => import('./pages/Memory'))
const LearnMore = lazy(() => import('./pages/LearnMore'))
const SocialRedirect = lazy(() => import('./pages/SocialRedirect'))
const Restore = lazy(() => import('./pages/Restore'))
const Admin = lazy(() => import('./pages/Admin'))

const Subnav = ({ showAdminContent }) => {
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const {
    memories_gallery: { isOn: showReactGallery },
    memories_dev: { isOn: showDevFeaturesFlag },
    memories_eol_disable_people: { isOn: hideMemoriesPeople },
    memories_eol_disable_find: { isOn: hideMemoriesFind },
  } = useFeatureFlags(['memories_gallery', 'memories_dev', 'memories_eol_disable_people', 'memories_eol_disable_find'])

  const handleReactPolymerRouting = useCallback((path) => {
    const origin = window.location.origin
    return urlUtils.isLocalHost(origin) ? path : `${origin}/photos${path}`
  }, [])

  return (
    <div style={{ display: atWidth({ default: 'none', md: 'inline' }) }}>
      <Tabs>
        <Tab title={t('memories.subnav.overview', 'Overview')} to="/" />
        <Tab
          title={t('memories.subnav.gallery', 'Gallery')}
          to={showReactGallery ? '/gallery' : handleReactPolymerRouting('/gallery')}
        />
        <Tab
          title={t('memories.subnav.people', 'People')}
          to={hideMemoriesPeople ? '/people' : handleReactPolymerRouting('/people')}
        />
        <Tab
          title={t('memories.subnav.find', 'Find')}
          to={hideMemoriesFind ? '/find' : handleReactPolymerRouting('/find')}
        />
        {showDevFeaturesFlag && <Tab title="Tree" to="/tree" />}
        {showDevFeaturesFlag && <Tab title="Restore" to="/restore" />}
        {showDevFeaturesFlag && showAdminContent && <Tab title="Admin" to="/admin" />}
      </Tabs>
    </div>
  )
}

function App({ setShowFooter, setFatFooter, setShowRestoreCommunityBanner }) {
  getAnonSession()
  const sessionId = getSessionId()
  const isCurrent = useRef(true)
  const [showSubnav, setShowSubnav] = useState(true)
  const [authorized, setAuthorized] = useState(false)

  const memoryViewerFlag = useGroupsFlags('memories_shared_memoryViewer')
  const {
    memories_gallery: { isOn: showReactGallery },
    memories_enableRestoreDashboard: { isOn: showRestorationTool },
    memories_dev: { isOn: showDevFeaturesFlag },
    memories_eol_disable_people: { isOn: hideMemoriesPeople },
    memories_eol_disable_find: { isOn: hideMemoriesFind },
  } = useFeatureFlags([
    'memories_gallery',
    'memories_enableRestoreDashboard',
    'memories_dev',
    'memories_eol_disable_people',
    'memories_eol_disable_find',
  ])

  useEffect(() => {
    const getAdminPermissions = async () => {
      try {
        const response = await casService.getPermissions({ context: 'ArtCtxOps', role: 'WebMemPrmAdmin', sessionId })
        isCurrent.current && setAuthorized(response?.data?.authorized && ['int', 'beta', 'local'].includes(targetEnv))
      } catch (error) {
        console.error(error)
      }
    }
    getAdminPermissions()
  }, [sessionId])

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  }, [])

  return (
    <ErrorBoundary>
      {showSubnav && <Subnav showAdminContent={authorized} />}

      <Suspense fallback={<NoticeLoading />}>
        <Switch>
          <Route exact requirePersonalData path="/">
            <Overview setFatFooter={setFatFooter} />
          </Route>
          {/* The gallery route needs to not be an AuthRoute so the /gallery/album page is public accessible. */}
          {showReactGallery && (
            <Route requirePersonalData path="/gallery">
              <GalleryProvider>
                <Gallery />
              </GalleryProvider>
            </Route>
          )}
          <AuthRoute requirePersonalData path="/people">
            {hideMemoriesPeople ? <Feedback /> : <People />}
          </AuthRoute>
          <AuthRoute requirePersonalData path="/find">
            {hideMemoriesFind ? <Feedback /> : <Find />}
          </AuthRoute>
          <Route path="/learnmore">
            <LearnMore />
          </Route>
          <Route requirePersonalData path="/socialRedirect">
            <SocialRedirect />
          </Route>
          {showRestorationTool && (
            <AuthRoute requirePersonalData path="/restore">
              <Restore setShowRestoreCommunityBanner={setShowRestoreCommunityBanner} />
            </AuthRoute>
          )}
          {memoryViewerFlag && (
            <Route requirePersonalData path="/memory/:id">
              <MemoryPage setShowSubnav={setShowSubnav} setShowFooter={setShowFooter} />
            </Route>
          )}
          {showDevFeaturesFlag && (
            <>
              <AuthRoute requirePersonalData path="/tree/:pid">
                <TreeMemoriesPage />
              </AuthRoute>
              {authorized && (
                <AuthRoute requirePersonalData path="/admin">
                  <Admin />
                </AuthRoute>
              )}
            </>
          )}
          <Route>
            <NotFound />
          </Route>
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}
export default App
